import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import {
  getLoginUrl,
  completeAuth,
  getAuthStatus,
  getKiteCredentials,
  getPositions,
  listStrategies,
  getSessionStatus,
  logout,
} from "./api";
import PositionsTable from "./components/PositionsTable";
import ManualTrackPanel from "./components/ManualTrackPanel";
import StrategyBuilder from "./components/StrategyBuilder";
import StrategyList from "./components/StrategyList";
import AccountPnlChart from "./components/AccountPnlChart";
import KiteSettings from "./components/KiteSettings";
import Login from "./components/Login";

const POSITIONS_POLL_MS = 2000;
const STRATEGIES_POLL_MS = 2000;

export default function App() {
  // null = still checking with the backend, true/false = known
  const [sessionAuthenticated, setSessionAuthenticated] = useState(null);
  const [username, setUsername] = useState(null);

  const [tab, setTab] = useState("positions"); // "positions" | "strategies" | "history" | "settings"

  const [positions, setPositions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [strategies, setStrategies] = useState([]);

  const [authenticated, setAuthenticated] = useState(false); // Kite (broker) auth
  const [hasKiteCredentials, setHasKiteCredentials] = useState(null); // null = unknown yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authMessage, setAuthMessage] = useState(null);

  const positionsPollRef = useRef(null);
  const strategiesPollRef = useRef(null);

  const checkSession = useCallback(() => {
    getSessionStatus()
      .then((res) => {
        setSessionAuthenticated(!!res.data.authenticated);
        setUsername(res.data.username || null);
      })
      .catch(() => setSessionAuthenticated(false));
  }, []);

  const checkKiteCredentials = useCallback(() => {
    getKiteCredentials()
      .then((res) => setHasKiteCredentials(!!res.data.has_credentials))
      .catch(() => {});
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // --- Handle Kite's redirect back to this app -----------------------
  // Assumption: the Kite app's Redirect URL is set to this frontend's
  // root. After login Kite appends ?request_token=...&action=login&
  // status=success to the URL. We read it once the dashboard session is
  // confirmed, exchange it for an access_token, then strip it from the
  // address bar. Gated on sessionAuthenticated so we don't try to hit a
  // protected endpoint before logging into the dashboard itself.
  useEffect(() => {
    if (!sessionAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const requestToken = params.get("request_token");
    if (!requestToken) return;

    setAuthMessage("Completing Kite login...");
    completeAuth(requestToken)
      .then(() => {
        setAuthMessage("Connected to Kite.");
        setAuthenticated(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      })
      .catch((err) => {
        setAuthMessage(
          `Failed to complete Kite login: ${err.response?.data?.detail || err.message}`
        );
      });
  }, [sessionAuthenticated]);

  useEffect(() => {
    if (!sessionAuthenticated) return;
    getAuthStatus()
      .then((res) => setAuthenticated(res.data.authenticated))
      .catch(() => {});
    checkKiteCredentials();
  }, [sessionAuthenticated, checkKiteCredentials]);

  const fetchPositions = useCallback(() => {
    getPositions()
      .then((res) => {
        setPositions(res.data.net || []);
        setError(null);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message));
  }, []);

  const fetchStrategies = useCallback(() => {
    listStrategies()
      .then((res) => setStrategies(res.data.strategies || []))
      .catch((err) => setError(err.response?.data?.detail || err.message));
  }, []);

  useEffect(() => {
    if (!sessionAuthenticated) return;
    fetchPositions();
    positionsPollRef.current = setInterval(fetchPositions, POSITIONS_POLL_MS);
    return () => clearInterval(positionsPollRef.current);
  }, [sessionAuthenticated, fetchPositions]);

  useEffect(() => {
    if (!sessionAuthenticated) return;
    fetchStrategies();
    strategiesPollRef.current = setInterval(fetchStrategies, STRATEGIES_POLL_MS);
    return () => clearInterval(strategiesPollRef.current);
  }, [sessionAuthenticated, fetchStrategies]);

  const handleConnectKite = async () => {
    if (!hasKiteCredentials) {
      setError("Save your own Kite API key and secret in Settings first, then try Connect Kite again.");
      setTab("settings");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getLoginUrl();
      window.open(res.data.login_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // ignore - we're clearing local state regardless
    }
    setSessionAuthenticated(false);
    setUsername(null);
    setPositions([]);
    setStrategies([]);
    setSelected([]);
    setAuthenticated(false);
    setHasKiteCredentials(null);
  };

  const toggleSymbol = (tradingsymbol) => {
    setSelected((prev) =>
      prev.includes(tradingsymbol)
        ? prev.filter((s) => s !== tradingsymbol)
        : [...prev, tradingsymbol]
    );
  };

  if (sessionAuthenticated === null) {
    return (
      <div className="app">
        <p className="muted" style={{ padding: 24 }}>Loading...</p>
      </div>
    );
  }

  if (!sessionAuthenticated) {
    return <Login onLoggedIn={() => setSessionAuthenticated(true)} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>Trade Your Strat</h1>
        <div className="header-actions">
          {username && <span className="muted">Logged in as {username}</span>}
          <span className={`status-pill ${authenticated ? "ok" : "warn"}`}>
            {authenticated ? "Connected to Kite" : "Not connected"}
          </span>
          <button onClick={handleConnectKite} disabled={loading}>
            {loading ? "Opening..." : "Connect Kite"}
          </button>
          <button onClick={handleLogout}>Log Out</button>
        </div>
      </header>

      {authMessage && <div className="banner info">{authMessage}</div>}
      {error && <div className="banner error">Error: {error}</div>}

      <nav className="tab-bar">
        <button
          className={tab === "positions" ? "tab-active" : ""}
          onClick={() => setTab("positions")}
        >
          Positions
        </button>
        <button
          className={tab === "strategies" ? "tab-active" : ""}
          onClick={() => setTab("strategies")}
        >
          Strategies
        </button>
        <button
          className={tab === "history" ? "tab-active" : ""}
          onClick={() => setTab("history")}
        >
          P&amp;L History
        </button>
        <button
          className={tab === "settings" ? "tab-active" : ""}
          onClick={() => setTab("settings")}
        >
          Settings
        </button>
      </nav>

      {tab === "positions" && (
        <main className="app-main">
          <section className="panel">
            <h2>Positions</h2>
            <PositionsTable positions={positions} selected={selected} onToggle={toggleSymbol} />
          </section>

          <section className="panel">
            <h2>Track Selected Legs</h2>
            <ManualTrackPanel
              positions={positions}
              selected={selected}
              onTracked={() => {
                setSelected([]);
                fetchStrategies();
                setTab("strategies");
              }}
            />
          </section>
        </main>
      )}

      {tab === "strategies" && (
        <main className="app-main">
          <section className="panel">
            <h2>Build a Strategy</h2>
            <StrategyBuilder onCreated={fetchStrategies} />
          </section>

          <section className="panel">
            <h2>Saved Strategies</h2>
            <StrategyList strategies={strategies} positions={positions} onRefresh={fetchStrategies} />
          </section>
        </main>
      )}

      {tab === "history" && (
        <main>
          <section className="panel">
            <h2>Account P&amp;L History</h2>
            <p className="hint" style={{ marginTop: -4 }}>
              Total P&amp;L across every net position in your Kite account, not just legs tracked
              by this dashboard. Snapshotted roughly every 15 seconds while the backend is running
              and connected to Kite.
            </p>
            <AccountPnlChart />
          </section>
        </main>
      )}

      {tab === "settings" && (
        <main>
          <section className="panel">
            <h2>Kite API settings</h2>
            <KiteSettings onSaved={checkKiteCredentials} />
          </section>
        </main>
      )}
    </div>
  );
}
